"use client";

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useConfirm } from "@/hooks/useConfirm";

const MP_FLASH: Record<string, string> = {
  connected: "Cuenta de Mercado Pago conectada correctamente.",
  denied: "No completaste la autorización en Mercado Pago.",
  invalid_state: "La sesión de conexión expiró. Hacé clic en \"Conectar con Mercado Pago\" para intentarlo de nuevo.",
  missing_config: "Falta configurar OAuth en el servidor (CLIENT_ID y SECRET de la app).",
  token_error: "Mercado Pago no devolvió un token. Revisá credenciales y URL de redirección en tu aplicación.",
  plan_required: "Los pagos con Mercado Pago están disponibles en el plan Pro o superior. Hacé upgrade para activarlos.",
  forbidden_agenda: "No tenés permisos sobre la agenda seleccionada.",
};

/** Mismo recurso que la landing (`integrations-marquee`). */
const MP_LOGO_SRC = "/brands/mercado-pago.png";

type AgendaMpStatus = {
  id: string;
  name: string;
  slug: string;
  isPrimary: boolean;
  plan: string;
  planAllowsPayments: boolean;
  mpConnected: boolean;
  mpTokenExpiresAt: string | null;
  mpMarketplaceFeePercent: number | null;
};

type ApiResponse = {
  agendas: AgendaMpStatus[];
  activeProfessionalId: string | null;
  oauthEnabled: boolean;
};

export function MercadoPagoForm({ mpFlash, mpAgendaId }: { mpFlash?: string; mpAgendaId?: string }) {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyAgenda, setBusyAgenda] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/agendas/mp-status",
      { credentials: "include" },
      refresh,
    );
    if (r.ok) {
      setData((await r.json()) as ApiResponse);
    }
    setLoading(false);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!mpFlash) return;
    if (!MP_FLASH[mpFlash]) return;
    if (mpFlash === "missing_config" && data?.oauthEnabled) {
      navigate("/dashboard/payments", { replace: true });
      return;
    }
    setMsg(MP_FLASH[mpFlash]);
  }, [mpFlash, data?.oauthEnabled, navigate]);

  async function clearForAgenda(agendaId: string) {
    if (
      !(await confirm(
        "¿Desconectar esta cuenta de Mercado Pago de esta agenda? Los cobros volverán a usar la cuenta de la plataforma, si está configurada.",
      ))
    )
      return;
    setBusyAgenda(agendaId);
    const r = await fetchWithSessionSync(
      "/api/dashboard/mercadopago",
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ clear: true, agendaId }),
      },
      refresh,
    );
    setBusyAgenda(null);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "No se pudo desconectar la cuenta.");
      return;
    }
    await load();
  }

  const primaryBtn = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    px: "3.5",
    py: "2",
    rounded: "lg",
    fontSize: "sm",
    fontWeight: "semibold",
    color: "white",
    bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
    _hover: { opacity: 0.95 },
  });

  const secondaryBtn = css({
    px: "3.5",
    py: "2",
    rounded: "lg",
    fontSize: "sm",
    fontWeight: "semibold",
    color: "#b91c1c",
    bg: "transparent",
    borderWidth: "1px",
    borderColor: "rgba(248,113,113,0.45)",
    cursor: "pointer",
    _hover: { bg: "rgba(248,113,113,0.06)" },
  });

  return (
    <div className={cx(cf.dashSurface, css({ p: "6", mb: "10" }))}>
      <div className={css({ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3", mb: "3" })}>
        <img
          src={MP_LOGO_SRC}
          alt="Mercado Pago"
          width={40}
          height={40}
          className={css({ h: "10", w: "10", flexShrink: 0, objectFit: "contain" })}
        />
        <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", m: 0 })}>
          Cobros con Mercado Pago
        </h2>
      </div>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "3", lineHeight: "1.6", maxW: "44rem" })}>
        Conectá una cuenta de Mercado Pago por <strong>cada agenda</strong>. Cada agenda procesa las señas y cobros con
        su propia cuenta y recibe la acreditación de forma independiente.
      </p>
      <p className={css({ fontSize: "xs", color: "#475569", mb: "4", lineHeight: "1.55", maxW: "44rem", bg: "#f8fafc", p: "3", rounded: "lg" })}>
        Cuando cobrás con tu cuenta vinculada, <strong>SF Turnos</strong> retiene un <strong>3%</strong> del monto en concepto de
        comisión (configurable por cuenta desde el panel del administrador).
      </p>

      {msg ? (
        <p className={css({ fontSize: "sm", color: "#4f46e5", mb: "3" })}>{msg}</p>
      ) : null}

      {loading && !data ? (
        <p className={css({ fontSize: "sm", color: "#64748b" })}>Cargando agendas…</p>
      ) : !data || data.agendas.length === 0 ? (
        <p className={css({ fontSize: "sm", color: "#64748b" })}>No hay agendas para mostrar.</p>
      ) : (
        <ul className={css({ display: "flex", flexDir: "column", gap: "3", listStyle: "none", m: 0, p: 0 })}>
          {data.agendas.map((a) => {
            const expiresLabel = formatTokenExpiry(a.mpTokenExpiresAt);
            const highlight = mpAgendaId === a.id && mpFlash === "connected";
            return (
              <li
                key={a.id}
                className={css({
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "3",
                  p: "3.5",
                  rounded: "lg",
                  borderWidth: "1px",
                  borderColor: highlight ? "token(colors.brand.500)" : "#e2e8f0",
                  bg: highlight ? "rgba(99,102,241,0.04)" : "white",
                })}
              >
                <div className={css({ minW: 0, display: "flex", flexDir: "column", gap: "1" })}>
                  <p className={css({ fontWeight: "semibold", color: "#0f172a", fontSize: "sm" })}>
                    {a.name}
                    {a.isPrimary ? (
                      <span className={css({ ml: "2", fontSize: "xs", color: "#64748b", fontWeight: "normal" })}>
                        (principal)
                      </span>
                    ) : null}
                    {a.id === data.activeProfessionalId ? (
                      <span className={css({ ml: "2", fontSize: "xs", color: "token(colors.brand.600)", fontWeight: "semibold" })}>
                        activa
                      </span>
                    ) : null}
                  </p>
                  <p className={css({ fontSize: "xs", color: "#64748b" })}>
                    /book/{a.slug} · Plan {a.plan}
                  </p>
                  {a.planAllowsPayments ? (
                    <p
                      className={css({
                        fontSize: "xs",
                        color: a.mpConnected ? "#15803d" : "#64748b",
                        mt: "0.5",
                      })}
                    >
                      {a.mpConnected ? "● Conectada" : "○ Sin conectar"}
                      {a.mpConnected && expiresLabel ? (
                        <span className={css({ color: "#64748b", ml: "2" })}>
                          (vence el {expiresLabel})
                        </span>
                      ) : null}
                    </p>
                  ) : (
                    <p className={css({ fontSize: "xs", color: "#b45309", mt: "0.5" })}>
                      Esta agenda está en plan {a.plan}; los pagos con Mercado Pago requieren Pro o Enterprise.
                    </p>
                  )}
                </div>
                <div className={css({ display: "flex", gap: "2", alignItems: "center", flexWrap: "wrap" })}>
                  {a.planAllowsPayments ? (
                    <>
                      <a
                        href={`/api/dashboard/mercadopago/oauth/start?agendaId=${encodeURIComponent(a.id)}`}
                        className={primaryBtn}
                      >
                        {a.mpConnected ? "Cambiar cuenta" : "Conectar"}
                      </a>
                      {a.mpConnected ? (
                        <button
                          type="button"
                          disabled={busyAgenda === a.id}
                          onClick={() => void clearForAgenda(a.id)}
                          className={secondaryBtn}
                        >
                          {busyAgenda === a.id ? "Desconectando…" : "Desconectar"}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <a
                      href="/dashboard/subscription"
                      className={css({
                        px: "3",
                        py: "2",
                        rounded: "lg",
                        fontSize: "xs",
                        fontWeight: "semibold",
                        color: "token(colors.brand.700)",
                        bg: "rgba(99,102,241,0.08)",
                        textDecoration: "none",
                        _hover: { bg: "rgba(99,102,241,0.15)" },
                      })}
                    >
                      Mejorar plan
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {data && !data.oauthEnabled ? (
        <p className={css({ fontSize: "xs", color: "#64748b", maxW: "44rem", lineHeight: "1.5", mt: "4" })}>
          Si al hacer clic te avisa que falta configuración, el administrador debe definir{" "}
          <code className={css({ color: "#334155", bg: "#f1f5f9", px: "1", rounded: "sm" })}>MERCADOPAGO_OAUTH_CLIENT_ID</code> y{" "}
          <code className={css({ color: "#334155", bg: "#f1f5f9", px: "1", rounded: "sm" })}>MERCADOPAGO_OAUTH_CLIENT_SECRET</code> y
          registrar la URL de redirección en la app de Mercado Pago (ver .env.example).
        </p>
      ) : null}
    </div>
  );
}

function formatTokenExpiry(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });
}
